<template>
	<section class="dp-layout department-config-page">
		<section class="dp-right">
			<p class="dp-dept-hint">
				<span class="dp-dept-hint__label">当前配置机构</span>
				<span class="dp-dept-hint__value">{{ currentDepartmentName || '总平台 (全部)' }}</span>
				<el-tag v-if="configSource === 'custom'" size="small" type="success"
					style="margin-left: 12px;">学校专属配置</el-tag>
				<el-tag v-else-if="configSource === 'db_default'" size="small" type="primary"
					style="margin-left: 12px;">系统默认配置 (SchoolId=0)</el-tag>
				<el-tag v-else-if="configSource === 'local_file'" size="small" type="warning"
					style="margin-left: 12px;">本地文件配置 (config.js)</el-tag>
			</p>
			<div class="editor-actions">
				<div class="editor-actions-left">
					<el-button v-if="editorMode === 'visual'" type="success" size="small" icon="el-icon-folder-add"
						@click="openAddCategoryDialog">
						新增配置分类
					</el-button>
					<el-button type="warning" plain size="small" icon="el-icon-picture-outline"
						@click="openCopyIconDialog">
						仅复制其他学校图标
					</el-button>
					<span v-if="editorMode === 'visual'" class="editor-hint">提示：分类与模块顺序即保存后的顺序，可用「上移/下移」调整。</span>
					<template v-else>
						<el-button type="primary" size="small" icon="el-icon-document-copy" @click="copyJsonConfig">
							复制 JSON 配置
						</el-button>
						<span class="editor-hint">提示：JSON 模式可直接复制/粘贴完整配置。</span>
					</template>
				</div>
				<div class="editor-actions-right">
					<el-radio-group v-model="editorMode" size="mini" @change="handleEditorModeChange">
						<el-radio-button label="visual">可视化编辑</el-radio-button>
						<el-radio-button label="json">JSON Editor</el-radio-button>
					</el-radio-group>
				</div>
			</div>
			<!-- 替换 JSON Editor，使用可视化配置界面 -->
			<div class="visual-editor-container" v-loading="pageLoading" element-loading-text="正在加载应用菜单配置...">
				<div class="visual-editor" v-if="editorMode === 'visual' && resultInfo && resultInfo.length">
					<el-card v-for="(category, cIndex) in resultInfo" :key="cIndex" class="category-card"
						shadow="never">
						<div slot="header" class="category-header">
							<div class="category-title-row">
								<span class="category-title">{{ category.title }}</span>
								<span class="category-order-btns">
									<el-button type="text" size="mini" icon="el-icon-top" :disabled="cIndex === 0"
										@click="moveCategoryUp(cIndex)" title="分类上移">上移</el-button>
									<el-button type="text" size="mini" icon="el-icon-bottom"
										:disabled="cIndex === resultInfo.length - 1" @click="moveCategoryDown(cIndex)"
										title="分类下移">下移</el-button>
								</span>
							</div>
							<!-- 分类级别操作：删除分类、在分类下新增菜单 -->
							<div class="category-actions">
								<el-button type="text" size="mini" icon="el-icon-plus"
									@click="openAddMenuDialog(cIndex, false)">新增应用模块</el-button>
								<el-button type="text" size="mini" icon="el-icon-folder-opened"
									@click="openAddMenuDialog(cIndex, true)">新增子菜单模块</el-button>
								<el-dropdown @command="handleCategoryCommand($event, cIndex)" trigger="click"
									style="margin-left: 10px;">
									<span class="el-dropdown-link"
										style="color: #F56C6C; cursor: pointer; font-size: 13px;">
										<i class="el-icon-delete"></i> 删除分类
									</span>
									<el-dropdown-menu slot="dropdown">
										<el-dropdown-item command="delete">确认删除该分类</el-dropdown-item>
									</el-dropdown-menu>
								</el-dropdown>
							</div>
						</div>
						<div class="menu-grid">
							<div v-for="(menu, mIndex) in category.list" :key="mIndex" class="menu-col">
								<div class="menu-item" :class="{ 'is-disabled': !menu.show }">
									<div class="menu-icon-wrap">
										<oss-el-image :src="getIconPath(menu.image)" :school-id="effectiveSchoolId"
											class="menu-icon" fit="contain" v-if="menu.image">
											<div slot="error" class="image-slot">
												<i class="el-icon-picture-outline"></i>
											</div>
										</oss-el-image>
										<i class="el-icon-menu" v-else></i>
									</div>
									<div class="menu-info-wrap">
										<div class="menu-name" :title="menu.title">
											{{ menu.title }}
											<el-tag v-if="menu.isVip" type="danger" size="mini" effect="dark"
												class="menu-vip-tag">专用</el-tag>
											<el-tag v-if="getAppLimitText(menu)" type="info" size="mini" effect="plain"
												class="menu-app-tag">{{ getAppLimitText(menu) }}</el-tag>
										</div>
										<div class="menu-role" :title="getRoleName(menu.role)">见: {{
											getRoleName(menu.role) }}</div>
										<div class="menu-link-row">
											<div class="menu-role-edit" @click="editRole(menu, cIndex, mIndex)">
												<i class="el-icon-edit"></i> 编辑角色
											</div>
											<div v-if="menu.target === '/pages/index/subMenu'"
												class="menu-role-edit menu-role-edit--sub"
												@click="openSubMenuConfigDialog(menu, cIndex, mIndex)">
												<i class="el-icon-setting"></i> 配置子菜单
											</div>
										</div>
									</div>
									<div class="menu-switch">
										<div class="menu-order-btns">
											<el-button type="text" size="mini" icon="el-icon-top" class="order-mini-btn"
												:disabled="mIndex === 0" @click="moveMenuUp(cIndex, mIndex)"
												title="模块上移"></el-button>
											<el-button type="text" size="mini" icon="el-icon-bottom"
												class="order-mini-btn" :disabled="mIndex === category.list.length - 1"
												@click="moveMenuDown(cIndex, mIndex)" title="模块下移"></el-button>
										</div>
										<el-switch v-model="menu.show" title="启用/禁用配置"></el-switch>
										<i class="el-icon-edit-outline menu-edit-icon"
											@click="openEditMenuDialog(menu, cIndex, mIndex)" title="编辑应用信息"></i>
										<i class="el-icon-delete menu-delete-icon"
											@click="handleDeleteMenu(cIndex, mIndex)" title="彻底删除此模块"></i>
									</div>
								</div>
							</div>
						</div>
					</el-card>
				</div>
				<el-empty v-else-if="editorMode === 'visual'" description="暂无配置项目，请选择左侧机构或重置配置"></el-empty>
				<div class="json-editor-wrap" v-else>
					<vue-json-editor ref="jsonEditorRef" class="department-json-editor" v-model="jsonEditorData"
						:show-btns="false" :expandedOnStart="true" mode="code" lang="zh"
						@json-change="onJsonEditorChange" @has-error="onJsonEditorHasError"></vue-json-editor>
				</div>
			</div>

			<!-- 角色编辑弹窗 -->
			<el-dialog title="编辑可见角色" :visible.sync="roleDialogVisible" width="400px" append-to-body>
				<div style="margin-bottom: 15px; color: #606266; font-size: 14px;">
					当前应用：<strong>{{ currentEditMenu ? currentEditMenu.title : '' }}</strong>
				</div>
				<el-select v-model="currentEditRoles" multiple filterable allow-create default-first-option
					placeholder="请选择或输入新增角色标签" style="width: 100%">
					<el-option v-for="item in defaultRoleOptions" :key="item.value" :label="item.label"
						:value="item.value">
					</el-option>
				</el-select>
				<div style="margin-top: 10px; font-size: 12px; color: #909399;">
					提示：您可以直接输入新的角色标识（如 prefect）并回车添加。
				</div>
				<span slot="footer" class="dialog-footer">
					<el-button @click="roleDialogVisible = false">取 消</el-button>
					<el-button type="primary" @click="saveRoleEdit">确 定</el-button>
				</span>
			</el-dialog>

			<!-- 新增分类弹窗 -->
			<el-dialog title="新增配置分类" :visible.sync="categoryDialogVisible" width="400px" append-to-body>
				<el-form :model="categoryForm" label-width="90px">
					<el-form-item label="分类名称">
						<el-input v-model="categoryForm.title" placeholder="如：系统管理、扩展应用等"></el-input>
					</el-form-item>
				</el-form>
				<span slot="footer" class="dialog-footer">
					<el-button @click="categoryDialogVisible = false">取 消</el-button>
					<el-button type="primary" @click="saveAddCategory">确 定</el-button>
				</span>
			</el-dialog>

			<!-- 新增/编辑应用模块弹窗 -->
			<el-dialog :title="isEditMenu ? '编辑应用模块' : '新增应用模块'" :visible.sync="menuDialogVisible" width="950px"
				append-to-body>
				<div style="display: flex; gap: 20px; align-items: flex-start;">
					<!-- 左侧 iPhone 原型预览 -->
					<div class="preview-panel"
						style="width: 380px; display: flex; flex-direction: column; align-items: center; background: #f8f9fa; border-radius: 8px; padding: 15px; border: 1px solid #e9ecef; flex-shrink: 0;">
						<div
							style="font-weight: bold; margin-bottom: 10px; font-size: 14px; color: #495057; display: flex; align-items: center; gap: 5px;">
							<i class="el-icon-mobile-phone"></i>
							页面路径总览
						</div>
						<!-- 手机原型容器 -->
						<div class="phone-frame"
							style="width: 350px; height: 580px; border: 12px solid #212529; border-radius: 36px; overflow: hidden; background: #fff; box-shadow: 0 10px 25px rgba(0,0,0,0.15); position: relative;">
							<iframe :src="iframeSrc" style="width: 100%; height: 100%; border: none;"></iframe>
						</div>

					</div>

					<!-- 右侧配置表单 -->
					<div style="flex: 1; min-width: 0;">
						<el-form :model="menuForm" label-width="95px" :rules="menuRules" ref="menuFormRef">
							<el-form-item label="模块名称" prop="title">
								<el-input v-model="menuForm.title" placeholder="输入应用名称"></el-input>
							</el-form-item>
							<el-form-item label="跳转类型">
								<el-select v-model="menuForm.type" placeholder="选择跳转类型" style="width: 100%"
									:disabled="isSubMenuForm">
									<el-option v-for="item in menuTypeOptions" :key="item.value" :label="item.label"
										:value="item.value"></el-option>
								</el-select>
							</el-form-item>
							<el-form-item label="跳转路径" prop="target">
								<el-input v-model="menuForm.target" :disabled="isSubMenuForm"
									:placeholder="isWeappMenuForm ? '如：pages/index/subscription' : '如：/pages/demo/index'"></el-input>
							</el-form-item>
							<template v-if="isWeappMenuForm">
								<el-form-item label="小程序AppId">
									<el-input v-model="menuForm.appId" placeholder="如：wx664dfc0aed132c0b"></el-input>
								</el-form-item>
								<el-form-item label="参数映射">
									<el-input type="textarea" v-model="menuForm.extraDataStr" :rows="4"
										placeholder='如：[{"key":"parentId","source":"user.ParentId"},{"key":"schoolId","source":"user.School.SchoolId"}]'></el-input>
									<div style="font-size: 12px; color: #909399; margin-top: 5px;">
										extraData 数组 JSON，key 为小程序路径参数名，source 为 store 字段路径。
									</div>
								</el-form-item>
							</template>
							<el-form-item label="模块图标" prop="image">
								<el-upload class="avatar-uploader" action="#" :auto-upload="false"
									:show-file-list="false" :on-change="handleMenuIconChange"
									accept=".png, .jpg, .jpeg, .svg, .gif">
									<oss-el-image v-if="deferredImageDisplay('menuIcon', menuForm.image)"
										:src="getIconPath(deferredImageDisplay('menuIcon', menuForm.image))"
										:school-id="effectiveSchoolId" class="avatar" fit="contain">
										<div slot="error" class="image-slot"><i class="el-icon-picture-outline"></i>
										</div>
									</oss-el-image>
									<i v-else class="el-icon-plus avatar-uploader-icon"></i>
								</el-upload>
								<div style="font-size: 12px; color: #909399; line-height: 1.5; margin-top: 5px;">
									点击虚线框上传或更改图标，推荐使用透明背景 PNG。<br />当前存值：{{ menuForm.image || '无' }}
								</div>
							</el-form-item>
							<el-form-item label="可见应用" prop="apps">
								<el-checkbox-group v-model="menuForm.apps">
									<el-checkbox label="wx">微信</el-checkbox>
									<el-checkbox label="dd">钉钉</el-checkbox>
								</el-checkbox-group>
							</el-form-item>
							<el-form-item label="默认可见" prop="role">
								<el-select v-model="menuForm.role" multiple filterable allow-create default-first-option
									@change="handleSelectChange('role')" placeholder="请选择可见角色" style="width: 100%">
									<el-option v-for="item in defaultRoleOptions" :key="item.value" :label="item.label"
										:value="item.value">
									</el-option>
								</el-select>
							</el-form-item>
							<!-- 新增：动态配置权限 -->
							<el-form-item label="动态权限"
								v-if="!isEditingSubMenuLevel && (isSubMenuForm || menuForm.target === '/pages/index/subMenu')">
								<el-checkbox-group v-model="selectedAuthKeys" @change="handleAuthKeysChange">
									<el-checkbox label="mallAdmin">商城管理员</el-checkbox>
									<el-checkbox label="scheduleAdmin">课表管理员(代申请)</el-checkbox>
									<el-checkbox label="dutyTeacher">值周教师</el-checkbox>
								</el-checkbox-group>
								<div style="margin-top: 5px;">
									<el-button type="text" size="mini" icon="el-icon-circle-plus-outline"
										@click="openCustomAuthDialog">添加自定义动态权限</el-button>
								</div>
								<!-- 展示已配置的动态权限列表 -->
								<div v-if="menuForm.authPermissions && menuForm.authPermissions.length"
									style="margin-top: 10px;">
									<div v-for="(auth, index) in menuForm.authPermissions" :key="index"
										style="font-size: 12px; color: #606266; background: #f4f4f5; padding: 6px 12px; border-radius: 4px; display: flex; justify-content: space-between; align-items: center; margin-bottom: 5px; border-left: 3px solid #2563eb;">
										<span>
											<strong>{{ auth.label || '自定义' }}</strong>:
											API: <code>{{ auth.apiKey }}</code> |
											<template v-if="auth.params && auth.params.length">
												参数: <span v-for="(p, pi) in auth.params"
													:key="pi"><code>{{ p.paramName }}</code>({{ p.paramUserKey }})
												</span> |
											</template>
											<template v-else>
												参数: <code>{{ auth.paramName }}</code>({{ auth.paramUserKey }}) |
											</template>
											返回路径: <code>{{ auth.valuePath }}</code>
										</span>
										<el-button type="text" size="mini" icon="el-icon-close"
											style="color: #f56c6c; padding: 0;"
											@click="removeAuthPermission(index)"></el-button>
									</div>
								</div>
							</el-form-item>

							<el-form-item label="限制条件">
								<el-select v-model="localConditions" multiple filterable allow-create
									default-first-option @change="handleSelectChange('localConditions')"
									placeholder="输入或选择定制条件 (如 classTeacher)" style="width: 100%">
									<el-option label="班主任 (classTeacher)" value="classTeacher"></el-option>
									<el-option label="宿舍管理员 (IsHostelAdmin)" value="IsHostelAdmin"></el-option>
									<el-option label="补助权限 (IsSubsidyAuth)" value="IsSubsidyAuth"></el-option>
									<el-option label="新闻审核 (IsTempNews)" value="IsTempNews"></el-option>
									<!-- 追加注入子菜单配置的动态权限供选择 -->
									<template
										v-if="isEditingSubMenuLevel && parentAuthPermissions && parentAuthPermissions.length">
										<el-option v-for="auth in parentAuthPermissions" :key="auth.apiKey"
											:label="`${auth.label || '动态权限'} (${auth.apiKey})`" :value="auth.apiKey">
										</el-option>
									</template>
								</el-select>
							</el-form-item>

							<el-form-item label="传参(query)" prop="queryStr">
								<el-input type="textarea" v-model="menuForm.queryStr" placeholder='如：{"type":"2"}'
									:rows="2"></el-input>
							</el-form-item>
							<el-form-item label="备注" prop="remake">
								<el-input v-model="menuForm.remake"></el-input>
							</el-form-item>
							<el-form-item label="是否专用" prop="isVip">
								<el-switch v-model="menuForm.isVip" active-text="是" inactive-text="否"></el-switch>
							</el-form-item>
						</el-form>
					</div>
				</div>
				<span slot="footer" class="dialog-footer">
					<el-button @click="menuDialogVisible = false">取 消</el-button>
					<el-button type="primary" @click="saveAddMenu">确 定</el-button>
				</span>
			</el-dialog>

			<!-- 自定义动态权限配置弹窗 -->
			<el-dialog title="添加自定义动态权限" :visible.sync="customAuthDialogVisible" width="550px" append-to-body>
				<el-form :model="customAuthForm" label-width="110px" ref="customAuthFormRef" :rules="customAuthRules">
					<el-form-item label="权限名称" prop="label">
						<el-input v-model="customAuthForm.label" placeholder="如：班级检查管理员"></el-input>
					</el-form-item>
					<el-form-item label="API 接口名" prop="apiKey">
						<el-input v-model="customAuthForm.apiKey" placeholder="对应 api/index.js 中的方法名称"></el-input>
					</el-form-item>
					<!-- 多组传参映射列表 -->
					<el-form-item label="参数映射">
						<div v-for="(param, index) in customAuthForm.params" :key="index"
							style="display: flex; gap: 10px; align-items: center; margin-bottom: 8px;">
							<el-input v-model="param.paramName" placeholder="传参字段名 (如 teacherId)"
								style="flex: 1;"></el-input>
							<span style="color: #909399;">→</span>
							<el-input v-model="param.paramUserKey" placeholder="用户映射属性 (如 UserId)"
								style="flex: 1;"></el-input>
							<el-button type="text" icon="el-icon-delete" style="color: #f56c6c; padding: 0;"
								:disabled="customAuthForm.params.length <= 1"
								@click="removeParamRow(index)"></el-button>
						</div>
						<div style="margin-top: 5px;">
							<el-button type="text" size="mini" icon="el-icon-plus"
								@click="addParamRow">添加参数映射</el-button>
						</div>
					</el-form-item>
					<el-form-item label="返回值路径" prop="valuePath">
						<el-input v-model="customAuthForm.valuePath"
							placeholder="获取返回结果路径，如 response.Enabled 或 response"></el-input>
					</el-form-item>
				</el-form>
				<span slot="footer" class="dialog-footer">
					<el-button @click="customAuthDialogVisible = false">取 消</el-button>
					<el-button type="primary" @click="saveCustomAuth">确 定</el-button>
				</span>
			</el-dialog>

			<!-- 配置子菜单配置弹窗 -->
			<el-dialog title="配置二级子菜单" :visible.sync="subMenuConfigVisible" width="950px" append-to-body>
				<div
					style="margin-bottom: 15px; color: #606266; font-size: 14px; padding: 10px; background-color: #f4f4f5; border-radius: 4px; border-left: 4px solid #909399;">
					当前主模块：<strong>{{ currentSubMenuParent ? currentSubMenuParent.title : '' }}</strong> ({{
						currentSubMenuParent ? currentSubMenuParent.target : '' }})
				</div>

				<div class="editor-actions" style="margin-bottom: 15px;">
					<el-button type="success" size="small" icon="el-icon-folder-add" @click="openAddSubCategoryDialog">
						新增子菜单分类
					</el-button>
					<span style="font-size: 12px; color: #909399;">提示：子菜单不支持再次嵌套子菜单。</span>
				</div>

				<div style="max-height: 55vh; overflow-y: auto; padding-right: 8px;">
					<el-card v-for="(category, cIdx) in currentSubMenuConfig" :key="cIdx" class="category-card"
						shadow="never" style="margin-bottom: 15px;">
						<div slot="header" class="category-header"
							style="display: flex; justify-content: space-between; align-items: center; padding: 8px 15px;">
							<div class="category-title-row" style="display: flex; align-items: center; gap: 8px;">
								<span class="category-title"
									style="font-weight: bold; border-left: 4px solid #67C23A; padding-left: 8px;">{{
										category.title }}</span>
								<span class="category-order-btns">
									<el-button type="text" size="mini" icon="el-icon-top" :disabled="cIdx === 0"
										@click="moveSubCategoryUp(cIdx)">上移</el-button>
									<el-button type="text" size="mini" icon="el-icon-bottom"
										:disabled="cIdx === currentSubMenuConfig.length - 1"
										@click="moveSubCategoryDown(cIdx)">下移</el-button>
								</span>
							</div>
							<div class="category-actions">
								<el-button type="text" size="mini" icon="el-icon-plus"
									@click="openAddSubMenuDialog(cIdx)">新增应用模块</el-button>
								<el-button type="text" size="mini" icon="el-icon-delete"
									style="color: #F56C6C; margin-left: 10px;"
									@click="deleteSubCategory(cIdx)">删除分类</el-button>
							</div>
						</div>

						<el-row :gutter="15">
							<el-col :xs="24" :sm="12" :md="8" :lg="8" v-for="(menu, mIdx) in category.list" :key="mIdx"
								class="menu-col" style="margin-bottom: 10px;">
								<div class="menu-item"
									style="border: 1px solid #ebeef5; padding: 10px; border-radius: 6px; display: flex; align-items: center; justify-content: space-between;">
									<div style="display: flex; align-items: center; min-width: 0; flex: 1;">
										<div class="menu-icon-wrap"
											style="width: 32px; height: 32px; border-radius: 6px; background: #f0f2f5; display: flex; align-items: center; justify-content: center; margin-right: 10px; flex-shrink: 0;">
											<oss-el-image :src="getIconPath(menu.image)" :school-id="effectiveSchoolId"
												style="width: 20px; height: 20px;" class="menu-icon" fit="contain"
												v-if="menu.image">
												<div slot="error"><i class="el-icon-picture-outline"></i></div>
											</oss-el-image>
											<i class="el-icon-menu" style="font-size: 16px; color: #909399;" v-else></i>
										</div>
										<div style="min-width: 0; flex: 1;">
											<div style="font-weight: bold; font-size: 13px; color: #303133; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; display: flex; align-items: center; gap: 4px;"
												:title="menu.title">
												<span style="overflow: hidden; text-overflow: ellipsis;">{{ menu.title
												}}</span>
												<el-tag v-if="getAppLimitText(menu)" type="info" size="mini"
													effect="plain"
													style="height: 18px; line-height: 16px; padding: 0 4px; font-size: 11px; flex-shrink: 0;">{{
														getAppLimitText(menu) }}</el-tag>
											</div>
											<div style="font-size: 11px; color: #909399; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;"
												:title="getRoleName(menu.role)">见: {{ getRoleName(menu.role) }}</div>
										</div>
									</div>
									<div
										style="display: flex; align-items: center; gap: 8px; flex-shrink: 0; margin-left: 8px;">
										<div style="display: flex; flex-direction: column;">
											<el-button type="text" size="mini" icon="el-icon-top"
												style="padding: 2px; margin: 0;" :disabled="mIdx === 0"
												@click="moveSubMenuUp(cIdx, mIdx)"></el-button>
											<el-button type="text" size="mini" icon="el-icon-bottom"
												style="padding: 2px; margin: 0;"
												:disabled="mIdx === category.list.length - 1"
												@click="moveSubMenuDown(cIdx, mIdx)"></el-button>
										</div>
										<el-switch v-model="menu.show" size="mini"
											style="margin-right: 2px;"></el-switch>
										<i class="el-icon-edit-outline"
											style="color: #e6a23c; cursor: pointer; font-size: 15px;"
											@click="openEditSubMenuDialog(menu, cIdx, mIdx)" title="编辑"></i>
										<i class="el-icon-delete"
											style="color: #f56c6c; cursor: pointer; font-size: 15px;"
											@click="deleteSubMenu(cIdx, mIdx)" title="删除"></i>
									</div>
								</div>
							</el-col>
						</el-row>
					</el-card>
					<el-empty v-if="!currentSubMenuConfig || !currentSubMenuConfig.length"
						description="暂无二级分类，请点击上方新增"></el-empty>
				</div>

				<span slot="footer" class="dialog-footer">
					<el-button @click="subMenuConfigVisible = false">取 消</el-button>
					<el-button type="primary" @click="saveSubMenuConfig">确 定</el-button>
				</span>
			</el-dialog>

			<!-- 仅同步其他学校图标弹窗 -->
			<el-dialog title="复制其他学校图标 (仅更新ICON)" :visible.sync="copyIconDialogVisible" width="850px" append-to-body
				:close-on-click-modal="false" top="6vh">
				<el-alert
					title="安全保障：此功能仅提取并更新模块的图标 (image)，绝不会改动或覆盖任何角色可见性 (role)、动态权限 (authPermissions)、模块显隐开关 (show) 等配置！"
					type="success" show-icon :closable="false" style="margin-bottom: 16px;">
				</el-alert>

				<el-tabs v-model="copyIconSourceType" type="card">
					<el-tab-pane label="从已有学校获取" name="school">
						<div style="display: flex; gap: 10px; align-items: center; margin: 10px 0;">
							<span style="font-size: 14px; font-weight: bold; white-space: nowrap;">选择来源学校：</span>
							<el-select v-model="sourceSchoolId" filterable placeholder="请选择或搜索学校（如：本地测试学校）"
								style="flex: 1;" :loading="schoolLoading">
								<el-option v-for="sch in schoolOptions" :key="sch.schoolId" :label="sch.label"
									:value="sch.schoolId" :disabled="sch.schoolId === DepartmentId">
									<span>{{ sch.label }}</span>
									<span v-if="sch.schoolId === DepartmentId"
										style="float: right; color: #909399; font-size: 12px;">(当前机构)</span>
								</el-option>
							</el-select>
							<el-button type="primary" icon="el-icon-download" :loading="sourceLoading"
								@click="fetchSourceSchoolConfig">
								读取图标配置
							</el-button>
						</div>
					</el-tab-pane>

					<el-tab-pane label="手动粘贴配置 JSON" name="json">
						<div style="margin: 10px 0;">
							<div style="font-size: 13px; color: #606266; margin-bottom: 6px;">
								请粘贴测试学校或其他环境导出的完整配置 JSON（仅提取其中的图标）：
							</div>
							<el-input type="textarea" v-model="sourceJsonText" :rows="4"
								placeholder="[ { &quot;title&quot;: &quot;管理&quot;, &quot;list&quot;: [ ... ] } ]"></el-input>
							<div style="margin-top: 8px; text-align: right;">
								<el-button type="primary" size="small" icon="el-icon-cpu" :loading="sourceLoading"
									@click="parseSourceJsonConfig">
									解析 JSON 并提取图标
								</el-button>
							</div>
						</div>
					</el-tab-pane>
				</el-tabs>

				<!-- 同步选项配置 -->
				<div class="copy-icon-options"
					style="background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 6px; padding: 12px 16px; margin: 12px 0;">
					<div style="display: flex; flex-wrap: wrap; gap: 20px; align-items: center;">
						<div style="display: flex; align-items: center; gap: 8px;">
							<span style="font-size: 13px; font-weight: bold; color: #475569;">覆盖规则：</span>
							<el-radio-group v-model="copyIconStrategy" size="small" @change="recomputeIconMatches">
								<el-radio label="overwrite">覆盖现有图标 (推荐)</el-radio>
								<el-radio label="emptyOnly">仅填补空图标</el-radio>
							</el-radio-group>
						</div>
						<div style="display: flex; align-items: center; gap: 8px;">
							<el-checkbox v-model="includeSubMenu" @change="recomputeIconMatches">
								包含二级子菜单模块 (subMenu)
							</el-checkbox>
							<el-tag size="mini" type="success" effect="plain" style="margin-left: 4px;">严格同分类匹配</el-tag>
						</div>
					</div>
				</div>

				<!-- 匹配与对比结果区 -->
				<div v-if="sourceIconIndex" class="copy-icon-result">
					<!-- 统计与操作过滤条 -->
					<div
						style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; flex-wrap: wrap; gap: 8px;">
						<div style="font-size: 13px; color: #475569;">
							匹配到模块 <strong>{{ iconMatches.length }}</strong> 个，
							待更新图标 <strong style="color: #67c23a; font-size: 15px;">{{ willUpdateCount }}</strong> 个，
							无需更新 <strong>{{ identicalCount }}</strong> 个。
						</div>
						<div style="display: flex; gap: 10px; align-items: center;">
							<el-checkbox v-model="onlyShowChanges" size="mini">仅看将更新的模块</el-checkbox>
							<el-input v-model="iconPreviewSearch" size="mini" placeholder="搜索模块名称..."
								prefix-icon="el-icon-search" style="width: 160px;" clearable></el-input>
							<el-button type="text" size="mini" @click="selectAllWillUpdate">全选待更新</el-button>
							<el-button type="text" size="mini" @click="clearAllSelection">全不选</el-button>
						</div>
					</div>

					<!-- 对比表格 -->
					<el-table ref="iconTableRef" :data="filteredIconMatches" row-key="id" border size="small"
						max-height="320" @selection-change="handleIconSelectionChange" style="width: 100%;">
						<el-table-column type="selection" :reserve-selection="true" width="45"
							align="center"></el-table-column>
						<el-table-column label="模块名称 / 层级" min-width="160">
							<template slot-scope="scope">
								<div>
									<span style="font-weight: bold;">{{ scope.row.title }}</span>
									<el-tag v-if="scope.row.isSubMenu" size="mini" type="info"
										style="margin-left: 5px;">子菜单</el-tag>
								</div>
								<div style="font-size: 11px; color: #94a3b8;">{{ scope.row.displayLocation }}</div>
							</template>
						</el-table-column>
						<el-table-column label="当前图标" width="120" align="center">
							<template slot-scope="scope">
								<div v-if="scope.row.currentIcon"
									style="display: flex; justify-content: center; align-items: center;">
									<oss-el-image :src="getIconPath(scope.row.currentIcon)"
										:school-id="effectiveSchoolId" style="width: 32px; height: 32px;" fit="contain"
										:preview-src-list="[getIconPath(scope.row.currentIcon)]">
										<div slot="error" class="image-slot"><i class="el-icon-picture-outline"></i>
										</div>
									</oss-el-image>
								</div>
								<span v-else style="color: #94a3b8; font-size: 12px;">(无图标)</span>
							</template>
						</el-table-column>
						<el-table-column label="来源新图标" width="120" align="center">
							<template slot-scope="scope">
								<div v-if="scope.row.newIcon"
									style="display: flex; justify-content: center; align-items: center;">
									<oss-el-image :src="getIconPath(scope.row.newIcon)"
										:school-id="sourceSchoolId || effectiveSchoolId"
										style="width: 32px; height: 32px;" fit="contain"
										:preview-src-list="[getIconPath(scope.row.newIcon)]">
										<div slot="error" class="image-slot"><i class="el-icon-picture-outline"></i>
										</div>
									</oss-el-image>
								</div>
								<span v-else style="color: #94a3b8; font-size: 12px;">(来源无)</span>
							</template>
						</el-table-column>
						<el-table-column label="变更状态" width="130" align="center">
							<template slot-scope="scope">
								<el-tag v-if="scope.row.status === 'updated'" type="success" size="mini">覆盖更新</el-tag>
								<el-tag v-else-if="scope.row.status === 'filled'" type="warning"
									size="mini">填补空图</el-tag>
								<el-tag v-else type="info" size="mini">图标已一致</el-tag>
							</template>
						</el-table-column>
					</el-table>
				</div>
				<div v-else style="padding: 30px 0; text-align: center; color: #94a3b8;">
					<i class="el-icon-picture-outline"
						style="font-size: 40px; margin-bottom: 8px; color: #cbd5e1; display: block;"></i>
					请在上方选择来源学校或粘贴配置 JSON，点击「读取/解析」后即可预览并勾选需要复制的图标
				</div>

				<span slot="footer" class="dialog-footer">
					<el-button @click="copyIconDialogVisible = false">取 消</el-button>
					<el-button type="success" :disabled="!selectedIconMatches || !selectedIconMatches.length"
						@click="applySelectedIcons">
						应用所选图标到当前配置 ({{ selectedIconMatches.length }} 项)
					</el-button>
				</span>
			</el-dialog>

			<div class="title" style="margin-top: 10px;">快捷操作</div>
			<div class="moduls">
				<div class="button">
					<div class="item copy-icon-btn" @click="openCopyIconDialog">仅复制其他学校图标</div>
					<div class="item save-btn" @click="onJsonSave">保存当前配置</div>
				</div>
			</div>
		</section>
	</section>
</template>
<script>
import vueJsonEditor from 'vue-json-editor'
import config from './config'

import { getAppMenuConfig, updateAppMenuConfig, getSchoolDepartmentTree } from '../../api/modules/department';
import { BaseApiUrl } from '../../api/base';
import { batchResolveOssMediaUrls, isAliyunOssHttpsUrl } from '../../utils/mediaUrl';

export default {
	components: {
		vueJsonEditor,
	},
	data() {
		return {
			pageLoading: false, // 页面数据加载遮罩，避免在接口未返回前呈现内容
			configSource: '', // 当前配置来源：'custom' 学校专属配置 | 'db_default' 数据库系统默认(SchoolId=0) | 'local_file' 本地文件(config.js)
			DepartmentId: '',
			currentDepartmentName: '',
			resultInfo: [],
			editorMode: 'visual',
			jsonEditorData: [],
			jsonEditorHasError: false,
			BaseApiUrl: BaseApiUrl, //网络前缀
			// 角色编辑相关
			roleDialogVisible: false,
			currentEditMenu: null,
			currentEditRoles: [],
			defaultRoleOptions: [
				{ label: '学生 (student)', value: 'student' },
				{ label: '普通教师 (teacher)', value: 'teacher' },
				{ label: '部门主管 (manager)', value: 'manager' },
				{ label: '年级组长 (prefect)', value: 'prefect' },
				{ label: '校长 (headmaster)', value: 'headmaster' }
			],

			// 新增控制相关
			categoryDialogVisible: false,
			categoryForm: { title: '' },

			menuDialogVisible: false,
			isEditMenu: false, // 标识当前是否在编辑现有模块
			targetCategoryIndex: -1,
			targetMenuIndex: -1, // 编辑时记录在具体哪个位置
			isSubMenuForm: false, // 标识当前是否子菜单大模块弹框，锁定 /pages/index/subMenu
			isEditingSubMenuLevel: false, // 标识当前是否在配置二级子菜单下级模块
			selectedAuthKeys: [], // 当前内置动态权限的临时勾选状态
			localConditions: [], // 本地普通限制条件临时变量
			customAuthDialogVisible: false, // 自定义动态权限对话框
			customAuthForm: {
				label: '',
				apiKey: '',
				params: [{ paramName: '', paramUserKey: 'UserId' }],
				valuePath: 'response'
			},
			customAuthRules: {
				label: [{ required: true, message: '请输入权限名称', trigger: 'blur' }],
				apiKey: [{ required: true, message: '请输入 API 接口方法名', trigger: 'blur' }],
				valuePath: [{ required: true, message: '请输入返回值路径', trigger: 'blur' }]
			},
			subMenuConfigVisible: false, // 配置二级子菜单对话框
			currentSubMenuParent: null, // 当前正在编辑子菜单的父模块引用
			currentSubMenuConfig: [], // 子菜单数据临时存储器，目录+列表的二级数据格式
			targetSubCategoryIndex: -1, // 正在编辑的子分类的 index
			targetSubMenuIndex: -1, // 正在编辑的子菜单模块的 index
			menuForm: {
				title: '',
				target: '',
				image: '',
				role: ['student', 'teacher'],
				apps: ['wx', 'dd'],
				type: 'local',
				show: true,
				isVip: false,
				appId: '',
				extraDataStr: '',
				condition: [],
				queryStr: '',
				remake: '',
				authPermissions: [] // 菜单项关联的动态权限列表
			},
			menuTypeOptions: [
				{ label: '本地页面 (local)', value: 'local' },
				{ label: '小程序跳转 (weapp)', value: 'weapp' }
			],
			menuRules: {
				title: [{ required: true, message: '请输入模块名称', trigger: 'blur' }],
				target: [{ required: true, message: '请输入目标跳转路径', trigger: 'blur' }]
			},

			// 仅复制其他学校图标相关状态
			copyIconDialogVisible: false,
			copyIconSourceType: 'school', // 'school' | 'json'
			schoolOptions: [],
			schoolLoading: false,
			sourceSchoolId: '',
			sourceJsonText: '',
			sourceLoading: false,
			sourceIconIndex: null, // 解析后的源图标索引数据结构
			copyIconStrategy: 'overwrite', // 'overwrite' | 'emptyOnly'
			includeSubMenu: true,
			iconMatches: [],
			selectedIconMatches: [],
			iconPreviewSearch: '',
			onlyShowChanges: false
		}
	},
	watch: {
		// 监听全局 Vuex 活跃学校 ID 的变化，重新载入配置数据
		'$store.state.activeSchoolId': {
			handler(newSchoolId) {
				if (newSchoolId && newSchoolId !== '0' && newSchoolId !== 0) {
					// 异步获取完整组织树，以匹配该学校真正的 SchId (如 sch_100)
					getSchoolDepartmentTree({ pid: 0, type: 1 }).then(res => {
						let matchedSchId = null;
						if (res.data && res.data.response) {
							matchedSchId = this.findSchIdByValue(res.data.response, newSchoolId);
						}
						// 优先使用查到的 SchId，若不存在则使用 newSchoolId 兜底
						this.DepartmentId = matchedSchId || String(newSchoolId);
						this.currentDepartmentName = this.$store.state.activeSchoolName || '总平台 (全部)'
						this.handleQuery()
					}).catch(err => {
						console.error('根据全局 ID 匹配真实学校 SchId 失败', err);
						// 异常时以原始 ID 进行兜底加载
						this.DepartmentId = String(newSchoolId);
						this.currentDepartmentName = this.$store.state.activeSchoolName || '总平台 (全部)'
						this.handleQuery()
					})
				} else {
					this.DepartmentId = '0'
					this.currentDepartmentName = '总平台 (全部)'
					this.handleQuery()
				}
			},
			immediate: true
		}
	},
	computed: {
		uploadHeaders() {
			let token = window.localStorage.Token || (this.$store && this.$store.state.token) || '';
			return {
				Authorization: 'Bearer ' + token
			};
		},
		iframeSrc() {
			return 'https://h5.yiqiba.xin/?agentId=3352452148&corpId=dinge6fd7f5295dcb3814ac5d6980864d335#/pages/index/pageList';
			//return 'http://localhost:8080/?agentId=3352452148&corpId=dinge6fd7f5295dcb3814ac5d6980864d335#/pages/index/pageList';
		},
		parentAuthPermissions() {
			if (!this.currentSubMenuParent) return [];
			const condition = this.currentSubMenuParent.condition;
			const rawConditions = Array.isArray(condition)
				? condition
				: (condition ? [condition] : []);
			const auths = [];
			rawConditions.forEach(c => {
				if (c && typeof c === 'object' && c.apiKey) {
					auths.push(c);
				}
			});
			return auths;
		},
		isWeappMenuForm() {
			return this.menuForm.type === 'weapp';
		},
		effectiveSchoolId() {
			if (this.DepartmentId && this.DepartmentId !== '0' && this.DepartmentId !== 0) {
				return String(this.DepartmentId);
			}
			const storeId = this.$store && this.$store.state && this.$store.state.activeSchoolId;
			if (storeId && storeId !== '0' && storeId !== 0) {
				return String(storeId);
			}
			return '';
		},
		willUpdateCount() {
			return this.iconMatches.filter(m => m.willUpdate).length;
		},
		identicalCount() {
			return this.iconMatches.filter(m => m.status === 'identical').length;
		},
		filteredIconMatches() {
			let list = this.iconMatches;
			if (this.onlyShowChanges) {
				list = list.filter(m => m.willUpdate);
			}
			if (this.iconPreviewSearch && this.iconPreviewSearch.trim()) {
				const keyword = this.iconPreviewSearch.trim().toLowerCase();
				list = list.filter(m =>
					(m.title && m.title.toLowerCase().includes(keyword)) ||
					(m.displayLocation && m.displayLocation.toLowerCase().includes(keyword))
				);
			}
			return list;
		}
	},
	methods: {
		// 递归寻找与全局学校 ID 匹配的学校节点，并返回其真实的 SchId 字段值
		findSchIdByValue(node, schoolId) {
			if (!node) return null;
			// 确保比对时均转为字符串，规避类型不一致导致匹配失败
			if (String(node.value) === String(schoolId)) {
				const orgType = String(node.DeptOrgType || node.deptOrgType || '').toLowerCase();
				if (orgType === 'school' || orgType === 'affiliateschool') {
					return node.SchId || node.schId || null;
				}
			}
			if (node.children && node.children.length) {
				for (let i = 0; i < node.children.length; i++) {
					const schId = this.findSchIdByValue(node.children[i], schoolId);
					if (schId) return schId;
				}
			}
			return null;
		},
		// 清洗下拉多选框自定义输入时的空字符与重复字符串
		handleSelectChange(field) {
			let val = this.menuForm[field];
			if (Array.isArray(val)) {
				// 获取干净的输入：将两端空格去除，丢弃全空内容
				let cleaned = val.map(item => typeof item === 'string' ? item.trim() : item).filter(item => item !== '');
				// 利用 Set 去重
				cleaned = [...new Set(cleaned)];
				// 如果处理前后不一样才重新更新值
				if (cleaned.length !== val.length || cleaned.some((v, i) => v !== val[i])) {
					this.menuForm[field] = cleaned;
				}
			}
		},
		handleMenuIconChange(file) {
			this.deferredImageChange('menuIcon', file, { foo: 'appmenupic', savedUrl: this.menuForm.image })
		},
		// 处理图片路径，将纯路径增加接口域名补全。最后更新：2026-09-06
		getIconPath(path) {
			if (!path) return '';
			if (/^(https?:|blob:|data:)/i.test(path)) {
				return path;
			}

			// 如果是移动端内置的静态图标（如 /static/levaReport.png），指向移动端 H5 线上静态资源
			if (path.startsWith('/static/') || path.startsWith('static/')) {
				const staticPath = path.startsWith('/') ? path : '/' + path;
				return 'https://h5.yiqiba.xin' + staticPath;
			}

			// 如果已经是后端上传返回的标准 appmenupic 路径，直接拼装域名后返回
			if (path.startsWith('/appmenupic/')) {
				return this.$resolveImageSrc(path);
			}

			// 防御纯斜杠，确保拼接出 /appmenupic/xxx.png 的完整网络路径
			let pureFileName = path;
			if (!pureFileName.startsWith('/')) {
				pureFileName = '/' + pureFileName;
			}

			return this.$resolveImageSrc('/appmenupic' + pureFileName);
		},
		/**
		 * 从后端接口获取指定机构/学校的应用菜单配置。最后更新：2026-09-06
		 * @param {string|number} pid 机构/学校 ID
		 * @returns {Promise<{ found: boolean, data: Array, schoolId: string, msg?: string, isNetworkError?: boolean }>}
		 */
		getConfig(pid) {
			return new Promise((resolve) => {
				getAppMenuConfig({ schoolId: pid })
					.then((res) => {
						if (res && res.data && res.data.success && res.data.response) {
							let data = []
							try {
								data = JSON.parse(res.data.response.ConfigJson || '[]')
							} catch (error) {
								console.error('[DepartmentConfig] 解析 ConfigJson 失败', error);
							}
							if (Array.isArray(data) && data.length > 0) {
								resolve({
									found: true,
									data: data,
									schoolId: String(res.data.response.SchoolId != null ? res.data.response.SchoolId : ''),
									msg: res.data.msg
								});
								return;
							}
						}
						// 接口返回未找到配置、或返回空列表，标识未配置
						const msg = (res && res.data && res.data.msg) || '';
						resolve({ found: false, data: [], schoolId: '', msg: msg });
					})
					.catch((error) => {
						console.error('[DepartmentConfig] 获取配置网络错误', error);
						resolve({ found: false, data: [], schoolId: '', isNetworkError: true });
					})
			})
		},
		cloneConfigData(data) {
			return JSON.parse(JSON.stringify(data || []));
		},
		// 复制当前配置 JSON 字符串到剪贴板
		copyJsonConfig() {
			const jsonStr = JSON.stringify(this.resultInfo || [], null, 2);
			if (navigator.clipboard && window.isSecureContext) {
				navigator.clipboard.writeText(jsonStr).then(() => {
					this.$message.success('配置 JSON 已成功复制到剪贴板');
				}).catch(() => {
					this.fallbackCopyText(jsonStr);
				});
			} else {
				this.fallbackCopyText(jsonStr);
			}
		},
		fallbackCopyText(text) {
			const textarea = document.createElement('textarea');
			textarea.value = text;
			textarea.style.position = 'fixed';
			textarea.style.opacity = '0';
			document.body.appendChild(textarea);
			textarea.select();
			try {
				document.execCommand('copy');
				this.$message.success('配置 JSON 已成功复制到剪贴板');
			} catch (err) {
				this.$message.error('复制失败，请手动选择文本复制');
			}
			document.body.removeChild(textarea);
		},
		handleEditorModeChange(mode) {
			if (mode === 'json') {
				this.jsonEditorData = this.cloneConfigData(this.resultInfo);
				this.jsonEditorHasError = false;
				return;
			}
			if (this.jsonEditorHasError) {
				// 如果从 JSON 模式切回可视化模式时存在格式错误，自动恢复为之前正确的版本并主动给予提示
				this.jsonEditorData = this.cloneConfigData(this.resultInfo);
				this.jsonEditorHasError = false;
				this.$message.warning('JSON 格式错误，已自动恢复到上一个有效版本');
				return;
			}
			this.resultInfo = this.cloneConfigData(this.jsonEditorData);
		},
		onJsonEditorChange(value) {
			// 只要成功触发 json-change，说明当前 JSON 格式解析正确，重置错误标志
			this.jsonEditorHasError = false;
			this.jsonEditorData = this.cloneConfigData(value);
			this.resultInfo = this.cloneConfigData(value);
		},
		onJsonEditorHasError(hasError) {
			this.jsonEditorHasError = !!hasError;
		},
		/** 分类：上移 */
		moveCategoryUp(cIndex) {
			if (cIndex <= 0 || !Array.isArray(this.resultInfo)) return;
			const arr = this.resultInfo;
			const item = arr.splice(cIndex, 1)[0];
			arr.splice(cIndex - 1, 0, item);
		},
		/** 分类：下移 */
		moveCategoryDown(cIndex) {
			if (!Array.isArray(this.resultInfo) || cIndex >= this.resultInfo.length - 1) return;
			const arr = this.resultInfo;
			const item = arr.splice(cIndex, 1)[0];
			arr.splice(cIndex + 1, 0, item);
		},
		/** 模块：上移 */
		moveMenuUp(cIndex, mIndex) {
			const list = this.resultInfo[cIndex] && this.resultInfo[cIndex].list;
			if (!list || mIndex <= 0) return;
			const item = list.splice(mIndex, 1)[0];
			list.splice(mIndex - 1, 0, item);
		},
		/** 模块：下移 */
		moveMenuDown(cIndex, mIndex) {
			const list = this.resultInfo[cIndex] && this.resultInfo[cIndex].list;
			if (!list || mIndex >= list.length - 1) return;
			const item = list.splice(mIndex, 1)[0];
			list.splice(mIndex + 1, 0, item);
		},
		// 保存配置
		onJsonSave() {
			if (!this.DepartmentId) {
				this.$message.warning('请先选择部门')
				return
			}
			if (this.editorMode === 'json' && this.jsonEditorHasError) {
				const fixed = this.formatAndFixJson();
				if (!fixed) {
					this.$message.error('JSON 格式有误，请修复后再保存')
					return
				}
			}

			// 转义全部特殊字符
			const sourceData = this.editorMode === 'json' ? this.jsonEditorData : this.resultInfo
			let data = JSON.stringify(sourceData)
			const loading = this.$loading({
				lock: true,
				text: '保存中',
				background: 'rgba(0, 0, 0, 0.7)',
			})

			// 保存数据
			const addModel = {
				SchoolId: this.DepartmentId,
				ConfigJson: data,
			}

			console.log(addModel)
			//调用接口保存配置
			updateAppMenuConfig(addModel)
				.then((res) => {
					if (res.data && res.data.success) {
						this.$message({
							message: '配置已更新',
							type: 'success',
						})
					} else {
						this.$message.error(res.data.msg || '更新配置失败')
					}
				})
				.catch((error) => {
					this.$message.error(error.message || '网络错误')
				})

			loading.close()
		},
		/**
		 * 查询部门配置：
		 * 阶梯加载策略（严格遵循用户规则）：
		 * 1. 优先调用 getAppMenuConfig({ schoolId: DepartmentId }) 获取该学校配置；
		 * 2. 如果后端查不到该学校配置，后端会自动降级查找 SchoolId = 0 并返回；
		 *    若后端由于网络或特殊原因未自动回退，前端也会主动调用 getAppMenuConfig({ schoolId: '0' }) 兜底；
		 * 3. 只有当接口连 SchoolId = 0 都没有配置（或接口完全无法访问），前端最后才使用本地的 config.js 兜底。
		 * 最后更新：2026-09-06
		 */
		async handleQuery() {
			if (!this.DepartmentId) {
				this.setData([])
				this.configSource = ''
				return
			}

			const currentSid = String(this.DepartmentId).trim()
			this.pageLoading = true
			try {
				// 第一步：调用接口查询当前机构
				const result = await this.getConfig(currentSid)

				// 检查第一步是否成功获取到了配置
				if (result && result.found && Array.isArray(result.data) && result.data.length > 0) {
					// 判断后端返回的具体是专属配置还是 SchoolId = 0 默认配置
					if (currentSid !== '0' && result.schoolId === currentSid) {
						// 1. 命中当前学校个性化专属配置
						this.configSource = 'custom'
						this.setData(result.data)
						return
					} else if (result.schoolId === '0') {
						// 2. 命中后端自动回退的 school = 0 默认配置
						this.configSource = 'db_default'
						this.setData(result.data)
						if (currentSid !== '0') {
							this.$message.info('当前机构暂无个性化配置，已沿用接口系统默认配置 (SchoolId = 0)')
						}
						return
					} else {
						// 其他情况，以接口返回为准
						this.configSource = 'custom'
						this.setData(result.data)
						return
					}
				}

				// 第二步：若第一步接口未返回配置且当前机构不是 '0'，主动再查一次 schoolId = 0
				if (currentSid !== '0') {
					const defaultResult = await this.getConfig('0')
					if (defaultResult && defaultResult.found && Array.isArray(defaultResult.data) && defaultResult.data.length > 0) {
						this.configSource = 'db_default'
						this.setData(defaultResult.data)
						this.$message.info('当前机构暂无个性化配置，已获取接口系统默认配置 (SchoolId = 0)')
						return
					}
				}

				// 第三步：若接口中连 schoolId = 0 都没有，最后才使用本地配置文件 config.js 兜底
				this.configSource = 'local_file'
				this.setData(JSON.parse(JSON.stringify(config)))
				if (result && result.isNetworkError) {
					this.$message.warning('网络连接异常，未能获取服务器配置，已使用本地基础模板展示')
				} else {
					this.$message.warning('接口未找到任何配置（包含 SchoolId=0），已载入本地基础配置文件兜底')
				}
			} finally {
				this.pageLoading = false
			}
		},
		// 设置数据
		setData(data) {
			this.resultInfo = Array.isArray(data) ? data : []
			this.jsonEditorData = this.cloneConfigData(this.resultInfo)
			this.jsonEditorHasError = false
			this.prefetchMenuIcons()
		},
		/**
		 * 批量预签名菜单图标（遵循性能优先规则，避免循环逐个请求）。最后更新：2026-09-06
		 */
		async prefetchMenuIcons() {
			const sid = this.effectiveSchoolId;
			if (!sid || !Array.isArray(this.resultInfo)) return;
			const ossUrls = [];
			const collect = (item) => {
				if (!item) return;
				const img = (item.image || '').trim();
				if (img && isAliyunOssHttpsUrl(img)) {
					ossUrls.push(img);
				}
				if (Array.isArray(item.subMenu)) {
					item.subMenu.forEach(subCat => {
						if (subCat && Array.isArray(subCat.list)) {
							subCat.list.forEach(collect);
						}
					});
				}
			};
			this.resultInfo.forEach(cat => {
				if (cat && Array.isArray(cat.list)) {
					cat.list.forEach(collect);
				}
			});
			if (ossUrls.length > 0) {
				try {
					await batchResolveOssMediaUrls([...new Set(ossUrls)], sid, { inline: true });
				} catch (err) {
					console.warn('[DepartmentConfig] 批量预签菜单图标异常', err);
				}
			}
		},
		/**
		 * 打开“复制其他学校图标”弹窗。最后更新：2026-09-06
		 */
		openCopyIconDialog() {
			this.copyIconDialogVisible = true;
			this.iconPreviewSearch = '';
			this.onlyShowChanges = false;
			this.loadSchoolOptions();
			// 如果已有来源索引，根据当前配置重新计算匹配
			if (this.sourceIconIndex) {
				this.recomputeIconMatches();
			}
		},

		/**
		 * 加载可供复制图标的学校列表。最后更新：2026-09-06
		 */
		async loadSchoolOptions() {
			if (this.schoolOptions && this.schoolOptions.length > 1) {
				return;
			}
			this.schoolLoading = true;
			try {
				const res = await getSchoolDepartmentTree({ pid: 0, type: 1 });
				const treeData = (res && res.data && res.data.response) || (res && res.response) || null;
				const list = [
					{ label: '总平台 (系统默认模板)', schoolId: '0', value: 0 }
				];

				const traverse = (node) => {
					if (!node) return;
					if (Array.isArray(node)) {
						node.forEach(child => traverse(child));
						return;
					}
					const orgType = String(node.DeptOrgType || node.deptOrgType || '').toLowerCase();
					if (orgType === 'school' || orgType === 'affiliateschool') {
						const schId = node.SchId || node.schId || String(node.value);
						list.push({
							label: node.label || node.name || node.Name,
							schoolId: String(schId),
							value: node.value
						});
					}
					if (node.children && node.children.length) {
						node.children.forEach(child => traverse(child));
					}
				};

				if (treeData) {
					traverse(treeData);
				}

				// 内存去重
				const uniqueSchools = [];
				const seenIds = new Set();
				list.forEach(s => {
					if (!seenIds.has(s.schoolId)) {
						seenIds.add(s.schoolId);
						uniqueSchools.push(s);
					}
				});
				this.schoolOptions = uniqueSchools;
			} catch (err) {
				console.error('获取学校列表失败', err);
			} finally {
				this.schoolLoading = false;
			}
		},

		/**
		 * 从指定学校读取配置并提取图标。最后更新：2026-09-06
		 */
		async fetchSourceSchoolConfig() {
			if (!this.sourceSchoolId) {
				this.$message.warning('请先选择要读取的来源学校');
				return;
			}
			this.sourceLoading = true;
			try {
				const res = await getAppMenuConfig({ schoolId: this.sourceSchoolId });
				if (res && res.data && res.data.success && res.data.response) {
					let configData = [];
					try {
						configData = JSON.parse(res.data.response.ConfigJson || '[]');
					} catch (e) {
						this.$message.error('解析该学校配置 JSON 失败');
						return;
					}
					if (!Array.isArray(configData) || !configData.length) {
						this.$message.warning('所选学校暂无配置数据或配置为空');
						return;
					}
					this.sourceIconIndex = this.buildSourceIconIndex(configData);
					this.recomputeIconMatches();
					this.$message.success(`成功读取来源学校配置，共识别 ${this.sourceIconIndex.totalIcons} 个有效图标`);
				} else {
					this.$message.error((res && res.data && res.data.msg) || '获取来源学校配置失败');
				}
			} catch (err) {
				console.error('读取来源学校配置失败', err);
				this.$message.error('网络错误，读取配置失败');
			} finally {
				this.sourceLoading = false;
			}
		},

		/**
		 * 从手动粘贴的 JSON 中提取图标。最后更新：2026-09-06
		 */
		parseSourceJsonConfig() {
			if (!this.sourceJsonText || !this.sourceJsonText.trim()) {
				this.$message.warning('请先粘贴配置 JSON');
				return;
			}
			this.sourceLoading = true;
			try {
				let parsed = null;
				try {
					parsed = JSON.parse(this.sourceJsonText.trim());
				} catch (err) {
					this.$message.error('JSON 格式解析失败，请检查是否为合法 JSON 数组');
					return;
				}

				if (!Array.isArray(parsed) || !parsed.length) {
					this.$message.error('输入的 JSON 必须是数组格式（即 [ { title: ..., list: [...] } ]）');
					return;
				}

				this.sourceIconIndex = this.buildSourceIconIndex(parsed);
				this.recomputeIconMatches();
				this.$message.success(`JSON 解析成功，共识别 ${this.sourceIconIndex.totalIcons} 个有效图标`);
			} finally {
				this.sourceLoading = false;
			}
		},

		/**
		 * 构建来源配置的多层级图标索引字典（严格分类/层级归属，禁止跨分类误匹配）。最后更新：2026-09-06
		 * @param {Array} sourceArray 来源配置数组
		 */
		buildSourceIconIndex(sourceArray) {
			// 一级模块索引（全部以 categoryTitle 为首要命名空间，严格同分类匹配）
			const catFullKeyMap = new Map();     // categoryTitle::target::queryStr::title -> { image, item }
			const catTargetQueryMap = new Map(); // categoryTitle::target::queryStr -> { image, item }
			const catTitleMap = new Map();       // categoryTitle::title -> { image, item }

			// 二级子菜单模块索引（严格限定在同一父模块 parentTitle 及子分类中）
			const subFullKeyMap = new Map();     // parentTitle::categoryTitle::title -> { image, item }
			const subTargetKeyMap = new Map();   // parentTitle::target::queryStr::title -> { image, item }
			const subTitleMap = new Map();       // parentTitle::title -> { image, item }

			let totalIcons = 0;

			const processItem = (item, categoryTitle, parentTitle = '', isSub = false) => {
				if (!item || typeof item !== 'object') return;
				const img = (item.image || '').trim();
				if (img) totalIcons++;

				const title = (item.title || '').trim();
				const target = (item.target || '').trim();
				const queryStr = item.query && typeof item.query === 'object' ? JSON.stringify(item.query) : '';
				const catKey = (categoryTitle || '').trim();
				const pKey = (parentTitle || '').trim();

				if (img) {
					if (isSub) {
						// 二级子菜单子模块：严格绑定 parentTitle 与子分类
						if (pKey && catKey && title) {
							subFullKeyMap.set(`${pKey}::${catKey}::${title}`, { image: img, item });
						}
						if (pKey && target && title) {
							subTargetKeyMap.set(`${pKey}::${target}::${queryStr}::${title}`, { image: img, item });
						}
						if (pKey && title && !subTitleMap.has(`${pKey}::${title}`)) {
							subTitleMap.set(`${pKey}::${title}`, { image: img, item });
						}
					} else {
						// 一级主菜单模块：严格归属 catKey 大分类，禁止脱离大分类跨分类匹配
						if (catKey && target && title) {
							const fKey = `${catKey}::${target}::${queryStr}::${title}`;
							if (!catFullKeyMap.has(fKey)) {
								catFullKeyMap.set(fKey, { image: img, item });
							}
						}
						if (catKey && target && target !== '/pages/index/subMenu') {
							const tKey = `${catKey}::${target}::${queryStr}`;
							if (!catTargetQueryMap.has(tKey)) {
								catTargetQueryMap.set(tKey, { image: img, item });
							}
						}
						if (catKey && title) {
							const cTitleKey = `${catKey}::${title}`;
							if (!catTitleMap.has(cTitleKey)) {
								catTitleMap.set(cTitleKey, { image: img, item });
							}
						}
					}
				}

				// 递归处理二级子菜单
				if (Array.isArray(item.subMenu)) {
					item.subMenu.forEach(subCat => {
						if (subCat && Array.isArray(subCat.list)) {
							const subCatTitle = (subCat.title || '').trim();
							subCat.list.forEach(subItem => {
								processItem(subItem, subCatTitle, title, true);
							});
						}
					});
				}
			};

			if (Array.isArray(sourceArray)) {
				sourceArray.forEach(cat => {
					if (cat && Array.isArray(cat.list)) {
						const catTitle = (cat.title || '').trim();
						cat.list.forEach(item => processItem(item, catTitle, '', false));
					}
				});
			}

			return {
				catFullKeyMap,
				catTargetQueryMap,
				catTitleMap,
				subFullKeyMap,
				subTargetKeyMap,
				subTitleMap,
				totalIcons
			};
		},

		/**
		 * 重新比对来源图标与当前学校模块（严格方案 A：同分类、同层级才算有效匹配）。最后更新：2026-09-06
		 */
		recomputeIconMatches() {
			if (!this.sourceIconIndex) return;
			const {
				catFullKeyMap,
				catTargetQueryMap,
				catTitleMap,
				subFullKeyMap,
				subTargetKeyMap,
				subTitleMap
			} = this.sourceIconIndex;
			const matches = [];

			const matchItem = (item, categoryTitle, parentTitle = '', isSub = false) => {
				if (!item || typeof item !== 'object') return;
				const currentIcon = (item.image || '').trim();
				const title = (item.title || '').trim();
				const target = (item.target || '').trim();
				const queryStr = item.query && typeof item.query === 'object' ? JSON.stringify(item.query) : '';
				const catKey = (categoryTitle || '').trim();
				const pKey = (parentTitle || '').trim();

				let matched = null;
				if (isSub) {
					// 1. 二级子菜单子项：优先在同父模块 + 同子分类 + 同名称下匹配
					if (pKey && catKey && title) {
						matched = subFullKeyMap.get(`${pKey}::${catKey}::${title}`);
					}
					// 2. 同父模块 + target + query + title 匹配
					if (!matched && pKey && target && title) {
						matched = subTargetKeyMap.get(`${pKey}::${target}::${queryStr}::${title}`);
					}
					// 3. 同父模块内 title 兜底
					if (!matched && pKey && title) {
						matched = subTitleMap.get(`${pKey}::${title}`);
					}
				} else {
					// 一级模块匹配：严格在同大分类下匹配，绝不跨分类乱拉图标
					// 1. 同大分类 + target + query + title 完全精确匹配
					if (catKey && target && title) {
						matched = catFullKeyMap.get(`${catKey}::${target}::${queryStr}::${title}`);
					}
					// 2. 同大分类 + target + query 匹配
					if (!matched && catKey && target && target !== '/pages/index/subMenu') {
						matched = catTargetQueryMap.get(`${catKey}::${target}::${queryStr}`);
					}
					// 3. 同大分类 + title 匹配
					if (!matched && catKey && title) {
						matched = catTitleMap.get(`${catKey}::${title}`);
					}
				}

				if (matched && matched.image) {
					const newIcon = matched.image;
					let status = 'identical'; // identical | updated | filled
					let willUpdate = false;

					if (currentIcon !== newIcon) {
						if (!currentIcon) {
							status = 'filled';
							willUpdate = true;
						} else {
							status = 'updated';
							willUpdate = this.copyIconStrategy === 'overwrite';
						}
					}

					matches.push({
						id: `${catKey}_${pKey}_${title}_${target}_${matches.length}`,
						refItem: item, // 直接引用当前模块响应式对象，精准更新
						title: title,
						categoryTitle: categoryTitle,
						parentTitle: parentTitle,
						isSubMenu: isSub,
						displayLocation: isSub
							? `[子菜单] ${parentTitle} > ${categoryTitle} > ${title}`
							: `${categoryTitle} > ${title}`,
						currentIcon: currentIcon,
						newIcon: newIcon,
						status: status,
						willUpdate: willUpdate
					});
				}

				// 处理二级子菜单
				if (this.includeSubMenu && Array.isArray(item.subMenu)) {
					item.subMenu.forEach(subCat => {
						if (subCat && Array.isArray(subCat.list)) {
							subCat.list.forEach(subItem => {
								matchItem(subItem, subCat.title || '子分类', title, true);
							});
						}
					});
				}
			};

			if (Array.isArray(this.resultInfo)) {
				this.resultInfo.forEach(cat => {
					if (cat && Array.isArray(cat.list)) {
						cat.list.forEach(item => {
							matchItem(item, cat.title || '分类', '', false);
						});
					}
				});
			}

			this.iconMatches = matches;
			if (this.sourceSchoolId && matches.length) {
				const sourceOssUrls = matches.map(m => m.newIcon).filter(u => u && isAliyunOssHttpsUrl(u));
				if (sourceOssUrls.length > 0) {
					batchResolveOssMediaUrls([...new Set(sourceOssUrls)], this.sourceSchoolId, { inline: true }).catch(() => { });
				}
			}
			this.$nextTick(() => {
				this.autoSelectChangeableItems();
			});
		},

		/**
		 * 自动勾选表格中需要更新图标的行。最后更新：2026-09-06
		 */
		autoSelectChangeableItems() {
			if (!this.$refs.iconTableRef) return;
			this.$refs.iconTableRef.clearSelection();
			this.filteredIconMatches.forEach(row => {
				if (row.willUpdate) {
					this.$refs.iconTableRef.toggleRowSelection(row, true);
				}
			});
		},

		/**
		 * 监听表格选择项变化。最后更新：2026-09-06
		 */
		handleIconSelectionChange(selection) {
			this.selectedIconMatches = selection;
		},

		/**
		 * 全选所有待更新项。最后更新：2026-09-06
		 */
		selectAllWillUpdate() {
			if (!this.$refs.iconTableRef) return;
			this.$refs.iconTableRef.clearSelection();
			this.filteredIconMatches.forEach(row => {
				if (row.willUpdate) {
					this.$refs.iconTableRef.toggleRowSelection(row, true);
				}
			});
		},

		/**
		 * 清空所有选择。最后更新：2026-09-06
		 */
		clearAllSelection() {
			if (!this.$refs.iconTableRef) return;
			this.$refs.iconTableRef.clearSelection();
		},

		/**
		 * 应用所选图标到当前配置中（仅修改 image，不触碰任何 role、show 等权限属性）。最后更新：2026-09-06
		 */
		applySelectedIcons() {
			if (!this.selectedIconMatches || !this.selectedIconMatches.length) {
				this.$message.warning('请至少勾选一个需要更新图标的模块');
				return;
			}

			let updatedCount = 0;
			this.selectedIconMatches.forEach(match => {
				if (match.refItem && match.newIcon && match.refItem.image !== match.newIcon) {
					this.$set(match.refItem, 'image', match.newIcon);
					updatedCount++;
				}
			});

			if (this.editorMode === 'json') {
				this.jsonEditorData = this.cloneConfigData(this.resultInfo);
			}

			this.copyIconDialogVisible = false;
			this.$message.success(
				`成功同步了 ${updatedCount} 个模块的图标！权限与功能设置完全保留。请确认页面卡片后，点击下方“保存当前配置”完成持久化。`
			);
		},

		// 获取角色中文名
		getRoleName(roles) {
			if (!roles || !roles.length) return '通用';
			const roleMap = {
				student: '学生',
				teacher: '普通教师',
				manager: '部门主管',
				prefect: '年级组长',
				headmaster: '校长'
			};
			return roles.map(r => roleMap[r] || r).join('、');
		},
		// 打开编辑角色弹窗
		editRole(menu, cIndex, mIndex) {
			this.currentEditMenu = menu;
			// 深拷贝当前角色列表，防止未保存直接修改了原数据
			this.currentEditRoles = Array.isArray(menu.role) ? [...menu.role] : [];
			this.roleDialogVisible = true;
		},
		// 确定保存角色修改
		saveRoleEdit() {
			if (this.currentEditMenu) {
				this.$set(this.currentEditMenu, 'role', this.currentEditRoles);
				this.$message.success('角色修改成功，请勿忘记点击下方"保存当前配置"');
			}
			this.roleDialogVisible = false;
		},
		// 删除分类
		handleCategoryCommand(command, cIndex) {
			if (command === 'delete') {
				this.resultInfo.splice(cIndex, 1);
				this.$message.success('分类已暂时移除，如需生效请点击"保存当前配置"');
			}
		},
		// 删除菜单项
		handleDeleteMenu(cIndex, mIndex) {
			this.$confirm('确定要删除此模块吗？', '提示', {
				type: 'warning'
			}).then(() => {
				this.resultInfo[cIndex].list.splice(mIndex, 1);
				this.$message.success('模块已删除，如需生效请点击"保存当前配置"');
			}).catch(() => { });
		},
		// 打开新增分类弹窗
		openAddCategoryDialog() {
			this.categoryForm.title = '';
			this.categoryDialogVisible = true;
		},
		// 保存新增分类
		saveAddCategory() {
			if (!this.categoryForm.title.trim()) {
				this.$message.error('分类名称不能为空');
				return;
			}
			this.resultInfo.push({
				title: this.categoryForm.title.trim(),
				list: []
			});
			this.$message.success('分类添加成功');
			this.categoryDialogVisible = false;
		},
		// 打开新增菜单弹窗
		openAddMenuDialog(cIndex, isSubMenuModule = false) {
			this.isEditMenu = false;
			this.targetCategoryIndex = cIndex;
			this.isSubMenuForm = isSubMenuModule;
			this.isEditingSubMenuLevel = false;
			this.selectedAuthKeys = [];
			this.localConditions = [];
			this.menuForm = {
				title: '',
				target: isSubMenuModule ? '/pages/index/subMenu' : '',
				image: '',
				role: ['student', 'teacher'],
				apps: ['wx', 'dd'],
				type: 'local',
				show: true,
				isVip: false,
				appId: '',
				extraDataStr: '',
				condition: [],
				queryStr: '',
				remake: '',
				authPermissions: []
			};
			this.deferredImageRevoke('menuIcon')
			this.menuDialogVisible = true;
			this.$nextTick(() => {
				this.$refs.menuFormRef && this.$refs.menuFormRef.clearValidate();
			});
		},
		// 打开编辑菜单弹窗
		openEditMenuDialog(menu, cIndex, mIndex) {
			this.isEditMenu = true;
			this.targetCategoryIndex = cIndex;
			this.targetMenuIndex = mIndex;
			this.isSubMenuForm = (menu.target === '/pages/index/subMenu');
			this.isEditingSubMenuLevel = false;

			// 深度拷贝要编辑的项，防止弹框打字时实时修改底层的界面
			let clonedMenu = JSON.parse(JSON.stringify(menu));

			// 解析 condition，区分本地普通条件（字符串）与动态权限（对象）
			const rawConditions = Array.isArray(clonedMenu.condition)
				? clonedMenu.condition
				: (clonedMenu.condition ? [clonedMenu.condition] : []);

			this.localConditions = [];
			const authPermissions = [];
			this.selectedAuthKeys = [];

			rawConditions.forEach(c => {
				if (typeof c === 'string') {
					this.localConditions.push(c);
				} else if (c && typeof c === 'object') {
					authPermissions.push(c);
					if (c.apiKey === 'getMoralMallPermission') this.selectedAuthKeys.push('mallAdmin');
					else if (c.apiKey === 'CheckScheduleAdminPermission') this.selectedAuthKeys.push('scheduleAdmin');
					else if (c.apiKey === 'checkDutyStaffPermissionForTeacher') this.selectedAuthKeys.push('dutyTeacher');
				}
			});

			clonedMenu.authPermissions = authPermissions;

			// 将实际的 query 对象转为字符串供界面供 JSON 编辑
			clonedMenu.queryStr = clonedMenu.query && typeof clonedMenu.query === 'object'
				? JSON.stringify(clonedMenu.query)
				: '';

			clonedMenu.extraDataStr = clonedMenu.extraData && Array.isArray(clonedMenu.extraData)
				? JSON.stringify(clonedMenu.extraData, null, 2)
				: '';

			clonedMenu.appId = clonedMenu.appId || '';
			clonedMenu.type = clonedMenu.type || 'local';
			if (clonedMenu.show === undefined) clonedMenu.show = true;
			if (clonedMenu.isVip === undefined) clonedMenu.isVip = false;
			clonedMenu.apps = Array.isArray(clonedMenu.apps) ? [...clonedMenu.apps] : ['wx', 'dd'];

			clonedMenu.remake = clonedMenu.remake || '';

			this.menuForm = clonedMenu;
			this.deferredImageInit('menuIcon', clonedMenu.image)

			this.menuDialogVisible = true;
			this.$nextTick(() => {
				this.$refs.menuFormRef && this.$refs.menuFormRef.clearValidate();
			});
		},
		// 内置动态权限复选框变动回调
		handleAuthKeysChange(keys) {
			const defaultAuths = {
				mallAdmin: { apiKey: 'getMoralMallPermission', paramName: 'teacherId', paramUserKey: 'UserId', valuePath: 'response', label: '商城管理员' },
				scheduleAdmin: { apiKey: 'CheckScheduleAdminPermission', paramName: 'teacherId', paramUserKey: 'UserId', valuePath: 'response.CanAgentApply', label: '课表管理员(代申请)' },
				dutyTeacher: { apiKey: 'checkDutyStaffPermissionForTeacher', paramName: 'teacherId', paramUserKey: 'UserId', valuePath: 'response', label: '值周教师' }
			};

			// 过滤掉当前现有的三大内置权限，保留自定义的权限
			const customAuths = (this.menuForm.authPermissions || []).filter(auth => {
				return !['getMoralMallPermission', 'CheckScheduleAdminPermission', 'checkDutyStaffPermissionForTeacher'].includes(auth.apiKey);
			});

			const newAuths = [];
			keys.forEach(k => {
				if (defaultAuths[k]) {
					newAuths.push({ ...defaultAuths[k] });
				}
			});

			this.menuForm.authPermissions = [...newAuths, ...customAuths];
		},
		// 打开自定义动态权限配置
		openCustomAuthDialog() {
			this.customAuthForm = {
				label: '',
				apiKey: '',
				params: [{ paramName: '', paramUserKey: 'UserId' }],
				valuePath: 'response'
			};
			this.customAuthDialogVisible = true;
			this.$nextTick(() => {
				this.$refs.customAuthFormRef && this.$refs.customAuthFormRef.clearValidate();
			});
		},
		// 增加参数映射行
		addParamRow() {
			this.customAuthForm.params.push({ paramName: '', paramUserKey: 'UserId' });
		},
		// 移除参数映射行
		removeParamRow(index) {
			if (this.customAuthForm.params.length > 1) {
				this.customAuthForm.params.splice(index, 1);
			}
		},
		// 保存自定义动态权限
		saveCustomAuth() {
			this.$refs.customAuthFormRef.validate(valid => {
				if (valid) {
					// 遍历并校验每组参数非空
					const validParams = [];
					for (let i = 0; i < this.customAuthForm.params.length; i++) {
						const p = this.customAuthForm.params[i];
						const pName = (p.paramName || '').trim();
						const pUserKey = (p.paramUserKey || '').trim();
						if (!pName || !pUserKey) {
							this.$message.error(`参数映射第 ${i + 1} 行参数名或用户映射字段不能为空`);
							return;
						}
						validParams.push({ paramName: pName, paramUserKey: pUserKey });
					}

					const newAuth = {
						apiKey: this.customAuthForm.apiKey.trim(),
						params: validParams,
						valuePath: this.customAuthForm.valuePath.trim(),
						label: this.customAuthForm.label.trim() || '自定义权限'
					};

					if (!this.menuForm.authPermissions) {
						this.$set(this.menuForm, 'authPermissions', []);
					}
					this.menuForm.authPermissions.push(newAuth);

					this.customAuthDialogVisible = false;
					this.$message.success('自定义动态权限添加成功');
				}
			});
		},
		// 移除某动态权限配置
		removeAuthPermission(index) {
			if (this.menuForm.authPermissions) {
				const removed = this.menuForm.authPermissions.splice(index, 1)[0];
				// 同步取消对应的内置 checkbox 勾选
				if (removed.apiKey === 'getMoralMallPermission') {
					this.selectedAuthKeys = this.selectedAuthKeys.filter(k => k !== 'mallAdmin');
				} else if (removed.apiKey === 'CheckScheduleAdminPermission') {
					this.selectedAuthKeys = this.selectedAuthKeys.filter(k => k !== 'scheduleAdmin');
				} else if (removed.apiKey === 'checkDutyStaffPermissionForTeacher') {
					this.selectedAuthKeys = this.selectedAuthKeys.filter(k => k !== 'dutyTeacher');
				}
			}
		},
		normalizeMenuFormPayload(newMenu) {
			if (newMenu.queryStr && newMenu.queryStr.trim()) {
				newMenu.query = JSON.parse(newMenu.queryStr);
			} else {
				delete newMenu.query;
			}
			delete newMenu.queryStr;

			// 可见应用：默认全选时不存该字段；取消的才显式保存在配置中
			const apps = Array.isArray(newMenu.apps) ? newMenu.apps : ['wx', 'dd'];
			if (apps.includes('wx') && apps.includes('dd')) {
				delete newMenu.apps;
			} else {
				newMenu.apps = apps;
			}

			if (newMenu.type === 'weapp') {
				if (newMenu.extraDataStr && newMenu.extraDataStr.trim()) {
					newMenu.extraData = JSON.parse(newMenu.extraDataStr);
				} else {
					delete newMenu.extraData;
				}
				if (!newMenu.appId) {
					delete newMenu.appId;
				}
			} else {
				delete newMenu.appId;
				delete newMenu.extraData;
			}
			delete newMenu.extraDataStr;
			return newMenu;
		},
		// 保存新增/编辑的应用菜单
		saveAddMenu() {
			this.$refs.menuFormRef.validate(async valid => {
				if (valid) {
					// 菜单图标统一上传至服务器本地 appmenupic 目录（不走私有 OSS），免签且多端全校均可直接公开访问。最后更新：2026-09-06
					const iconUrl = await this.deferredImageEnsure('menuIcon', { foo: 'appmenupic', noOss: true, currentUrl: this.menuForm.image })
					this.menuForm.image = iconUrl ? this.$resolveImageSrc(iconUrl) : ''
					// 核心拦截：如果当前正在编辑的是二级子菜单下级模块，直接将其写入二级配置
					if (this.isEditingSubMenuLevel) {
						if (!this.currentSubMenuConfig[this.targetSubCategoryIndex].list) {
							this.$set(this.currentSubMenuConfig[this.targetSubCategoryIndex], 'list', []);
						}

						const newMenu = JSON.parse(JSON.stringify(this.menuForm));
						try {
							this.normalizeMenuFormPayload(newMenu);
						} catch (e) {
							this.$message.error(e.message || '菜单配置 JSON 格式不正确');
							return;
						}

						// 合并本地限制条件与已选动态权限（如果 localConditions 里含有父级动态权限的 apiKey，则转换为完整对象）
						const finalConditions = [];
						if (this.localConditions && this.localConditions.length) {
							this.localConditions.forEach(cond => {
								const parentAuth = this.parentAuthPermissions.find(auth => auth.apiKey === cond);
								if (parentAuth) {
									// 如果是父大类配置过的动态权限 apiKey，深拷贝其完整配置对象存入 condition 数组
									finalConditions.push(JSON.parse(JSON.stringify(parentAuth)));
								} else {
									// 普通限制条件则保留为字符串
									finalConditions.push(cond);
								}
							});
						}

						if (finalConditions.length === 0) {
							delete newMenu.condition;
						} else if (finalConditions.length === 1 && typeof finalConditions[0] === 'string') {
							newMenu.condition = finalConditions[0];
						} else {
							newMenu.condition = finalConditions;
						}

						// 二级子菜单模块不能再含有子菜单，且移除转录字段
						delete newMenu.subMenu;
						delete newMenu.authPermissions;

						if (this.isEditMenu) {
							this.$set(this.currentSubMenuConfig[this.targetSubCategoryIndex].list, this.targetSubMenuIndex, newMenu);
							this.$message.success('子菜单应用模块信息修改成功');
						} else {
							this.currentSubMenuConfig[this.targetSubCategoryIndex].list.push(newMenu);
							this.$message.success('子菜单应用模块添加成功');
						}

						this.menuDialogVisible = false;
						return;
					}

					// 主菜单模块的保存逻辑
					if (!this.resultInfo[this.targetCategoryIndex].list) {
						this.$set(this.resultInfo[this.targetCategoryIndex], 'list', []);
					}

					const newMenu = JSON.parse(JSON.stringify(this.menuForm));

					try {
						this.normalizeMenuFormPayload(newMenu);
					} catch (e) {
						this.$message.error(e.message || '菜单配置 JSON 格式不正确，请检查双引号和括号，保存失败');
						return;
					}

					// 合并主大类限制条件和动态权限：只允许子菜单模块（L216/L226 规则）包含动态权限
					const finalConditions = [...this.localConditions];
					const isSubMenu = newMenu.target === '/pages/index/subMenu';
					const auths = this.menuForm.authPermissions || [];

					if (isSubMenu && auths.length) {
						auths.forEach(auth => {
							finalConditions.push(auth);
						});
					}

					if (finalConditions.length === 0) {
						delete newMenu.condition;
					} else if (finalConditions.length === 1 && typeof finalConditions[0] === 'string') {
						newMenu.condition = finalConditions[0];
					} else {
						newMenu.condition = finalConditions;
					}

					// 清除说明空字符串和辅助权限字段
					if (!newMenu.remake) {
						delete newMenu.remake;
					}
					delete newMenu.authPermissions;

					if (this.isEditMenu) {
						// 编辑模式：直接替换原位置
						this.$set(this.resultInfo[this.targetCategoryIndex].list, this.targetMenuIndex, newMenu);
						this.$message.success('应用模块信息修改成功，如需生效请点击"保存当前配置"');
					} else {
						// 新增模式：推入最后
						this.resultInfo[this.targetCategoryIndex].list.push(newMenu);
						this.$message.success('应用模块添加成功，如需生效请点击"保存当前配置"');
					}

					this.menuDialogVisible = false;
				}
			});
		},
		// 打开配置二级子菜单对话框
		openSubMenuConfigDialog(menu, cIndex, mIndex) {
			this.currentSubMenuParent = menu;
			this.currentSubMenuConfig = JSON.parse(JSON.stringify(menu.subMenu || []));
			this.subMenuConfigVisible = true;
		},
		// 确定保存二级子菜单配置
		saveSubMenuConfig() {
			if (this.currentSubMenuParent) {
				this.$set(this.currentSubMenuParent, 'subMenu', this.currentSubMenuConfig);
				this.$message.success('二级子菜单配置已保存，请点击页面下方"保存当前配置"以提交持久化');
			}
			this.subMenuConfigVisible = false;
		},
		// 新增二级子分类
		openAddSubCategoryDialog() {
			this.$prompt('请输入子菜单分类名称（如：核心功能、高级工具等）', '新增子菜单分类', {
				confirmButtonText: '确定',
				cancelButtonText: '取消',
				inputPattern: /\S+/,
				inputErrorMessage: '分类名称不能为空'
			}).then(({ value }) => {
				this.currentSubMenuConfig.push({
					title: value.trim(),
					list: []
				});
				this.$message.success('子分类新增成功');
			}).catch(() => { });
		},
		/** 二级子分类：上移 */
		moveSubCategoryUp(cIdx) {
			if (cIdx <= 0 || !Array.isArray(this.currentSubMenuConfig)) return;
			const arr = this.currentSubMenuConfig;
			const item = arr.splice(cIdx, 1)[0];
			arr.splice(cIdx - 1, 0, item);
		},
		/** 二级子分类：下移 */
		moveSubCategoryDown(cIdx) {
			if (!Array.isArray(this.currentSubMenuConfig) || cIdx >= this.currentSubMenuConfig.length - 1) return;
			const arr = this.currentSubMenuConfig;
			const item = arr.splice(cIdx, 1)[0];
			arr.splice(cIdx + 1, 0, item);
		},
		/** 二级子分类：删除 */
		deleteSubCategory(cIdx) {
			this.$confirm('确定要删除此子菜单分类及其下属所有模块吗？', '提示', {
				type: 'warning'
			}).then(() => {
				this.currentSubMenuConfig.splice(cIdx, 1);
				this.$message.success('分类已删除');
			}).catch(() => { });
		},
		// 二级子模块新增弹框
		openAddSubMenuDialog(cIdx) {
			this.isEditingSubMenuLevel = true;
			this.isSubMenuForm = false;
			this.isEditMenu = false;
			this.targetSubCategoryIndex = cIdx;
			this.selectedAuthKeys = [];
			this.localConditions = [];
			this.menuForm = {
				title: '',
				target: '',
				image: '',
				role: ['student', 'teacher'],
				apps: ['wx', 'dd'],
				type: 'local',
				show: true,
				isVip: false,
				appId: '',
				extraDataStr: '',
				condition: [],
				queryStr: '',
				remake: '',
				authPermissions: []
			};
			this.deferredImageRevoke('menuIcon')
			this.menuDialogVisible = true;
			this.$nextTick(() => {
				this.$refs.menuFormRef && this.$refs.menuFormRef.clearValidate();
			});
		},
		// 二级子模块编辑弹框
		openEditSubMenuDialog(menu, cIdx, mIdx) {
			this.isEditingSubMenuLevel = true;
			this.isSubMenuForm = false;
			this.isEditMenu = true;
			this.targetSubCategoryIndex = cIdx;
			this.targetSubMenuIndex = mIdx;

			let clonedMenu = JSON.parse(JSON.stringify(menu));

			// 解析 condition 区分字符串和动态权限对象（在二级应用中将动态权限对象解析为其 apiKey 塞入 localConditions 字符串列表中）
			const rawConditions = Array.isArray(clonedMenu.condition)
				? clonedMenu.condition
				: (clonedMenu.condition ? [clonedMenu.condition] : []);

			this.localConditions = [];
			this.selectedAuthKeys = [];

			rawConditions.forEach(c => {
				if (typeof c === 'string') {
					this.localConditions.push(c);
				} else if (c && typeof c === 'object' && c.apiKey) {
					// 塞入 apiKey 字符串，用于在限制条件下拉选项中匹配高亮
					this.localConditions.push(c.apiKey);
				}
			});

			clonedMenu.authPermissions = [];

			clonedMenu.queryStr = clonedMenu.query && typeof clonedMenu.query === 'object'
				? JSON.stringify(clonedMenu.query)
				: '';

			clonedMenu.extraDataStr = clonedMenu.extraData && Array.isArray(clonedMenu.extraData)
				? JSON.stringify(clonedMenu.extraData, null, 2)
				: '';

			clonedMenu.appId = clonedMenu.appId || '';
			clonedMenu.type = clonedMenu.type || 'local';
			if (clonedMenu.show === undefined) clonedMenu.show = true;
			if (clonedMenu.isVip === undefined) clonedMenu.isVip = false;
			clonedMenu.apps = Array.isArray(clonedMenu.apps) ? [...clonedMenu.apps] : ['wx', 'dd'];

			clonedMenu.remake = clonedMenu.remake || '';

			this.menuForm = clonedMenu;
			this.deferredImageInit('menuIcon', clonedMenu.image)
			this.menuDialogVisible = true;
			this.$nextTick(() => {
				this.$refs.menuFormRef && this.$refs.menuFormRef.clearValidate();
			});
		},
		/** 二级子模块：上移 */
		moveSubMenuUp(cIdx, mIdx) {
			const list = this.currentSubMenuConfig[cIdx] && this.currentSubMenuConfig[cIdx].list;
			if (!list || mIdx <= 0) return;
			const item = list.splice(mIdx, 1)[0];
			list.splice(mIdx - 1, 0, item);
		},
		/** 二级子模块：下移 */
		moveSubMenuDown(cIdx, mIdx) {
			const list = this.currentSubMenuConfig[cIdx] && this.currentSubMenuConfig[cIdx].list;
			if (!list || mIdx >= list.length - 1) return;
			const item = list.splice(mIdx, 1)[0];
			list.splice(mIdx + 1, 0, item);
		},
		/** 二级子模块：删除 */
		deleteSubMenu(cIdx, mIdx) {
			this.$confirm('确定要删除此子应用模块吗？', '提示', {
				type: 'warning'
			}).then(() => {
				this.currentSubMenuConfig[cIdx].list.splice(mIdx, 1);
				this.$message.success('子模块已删除');
			}).catch(() => { });
		},
		// 获取应用限制提示标签文本（取消的才显式存在 menu.apps 中）
		getAppLimitText(menu) {
			if (!menu || !Array.isArray(menu.apps)) return '';
			if (menu.apps.includes('wx') && !menu.apps.includes('dd')) return '仅微信';
			if (!menu.apps.includes('wx') && menu.apps.includes('dd')) return '仅钉钉';
			if (!menu.apps.includes('wx') && !menu.apps.includes('dd')) return '不可见';
			return '';
		},
	},
}
</script>
<style lang="stylus">
.department-config-page
  /* 布局由 split-layout.css 中 dp-layout / dp-right 统一控制 */

  > .dp-right
    overflow-y: hidden

  /* 可视化配置器样式 */
  .visual-editor-container
    padding-right: 6px

  .category-card
    margin-bottom: 12px
    border: 1px solid #ebeef5

    .el-card__header
      padding: 10px 14px
      background-color: #f8f9fa

    .el-card__body
      padding: 12px 14px

    .category-header
      display: flex
      justify-content: space-between
      align-items: flex-start
      flex-wrap: wrap
      gap: 8px 12px

      .category-title
        font-weight: bold
        color: #303133
        font-size: 15px
        border-left: 4px solid #2563eb
        padding-left: 10px

  .category-actions
    display: flex
    flex-wrap: wrap
    align-items: center
    gap: 4px 8px

  /* 按内容区宽度自动换列，避免窄屏仍强行 4 列挤扁 */
  .menu-grid
    display: grid
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr))
    gap: 12px

  .menu-col
    min-width: 0

  .menu-item
    display: flex
    align-items: flex-start
    gap: 10px
    min-width: 0
    padding: 12px
    background: #ffffff
    border: 1px solid #ebeef5
    border-radius: 8px
    transition: all 0.3s
    box-sizing: border-box

    &:hover
      box-shadow: 0 2px 10px rgba(0,0,0,0.05)
      border-color: #dcdfe6

    &.is-disabled
      background: #fafafa
      opacity: 0.7

      .menu-icon-wrap
        filter: grayscale(100%)
      .menu-info-wrap
        opacity: 0.6

  .menu-icon-wrap
    width: 40px
    height: 40px
    border-radius: 8px
    background: #f0f2f5
    display: flex
    align-items: center
    justify-content: center
    flex-shrink: 0

    .menu-icon
      width: 24px
      height: 24px

    i
      font-size: 20px
      color: #909399

  .menu-info-wrap
    flex: 1
    min-width: 0

    .menu-name
      font-size: 14px
      color: #303133
      font-weight: 500
      white-space: nowrap
      overflow: hidden
      text-overflow: ellipsis
      margin-bottom: 4px
      line-height: 1.4

    .menu-vip-tag
      margin-left: 5px
      height: 18px
      line-height: 16px
      padding: 0 4px
      vertical-align: middle

    .menu-app-tag
      margin-left: 4px
      height: 18px
      line-height: 16px
      padding: 0 4px
      vertical-align: middle

    .menu-role
      font-size: 12px
      color: #909399
      white-space: nowrap
      overflow: hidden
      text-overflow: ellipsis
      line-height: 1.4

  .menu-link-row
    display: flex
    flex-wrap: wrap
    gap: 8px 12px
    margin-top: 6px

  .menu-role-edit
    font-size: 12px
    color: #2563eb
    cursor: pointer
    display: inline-flex
    align-items: center
    gap: 2px
    white-space: nowrap
    line-height: 1.4

    &:hover
      text-decoration: underline

    &--sub
      color: #67C23A

  .menu-switch
    flex-shrink: 0
    display: flex
    align-items: center
    gap: 2px

    .menu-delete-icon, .menu-edit-icon
      margin-left: 8px
      font-size: 15px
      cursor: pointer
      opacity: 0.8

      &:hover
        opacity: 1
        transform: scale(1.1)

    .menu-delete-icon
      color: #f56c6c

    .menu-edit-icon
      color: #e6a23c

  .title
    font-size: 16px
    font-weight: 500
    color: #303133
    margin-bottom: 12px
    padding-bottom: 8px
    border-bottom: 1px solid #ebeef5

  .moduls
    padding: 0

  .editor-hint
    margin-left: 12px
    font-size: 12px
    color: #909399
    vertical-align: middle

  .editor-actions
    display: flex
    justify-content: space-between
    align-items: center
    flex-wrap: wrap
    gap: 10px
    margin-bottom: 12px
    flex-shrink: 0

  .editor-actions-left
    display: flex
    align-items: center
    flex-wrap: wrap
    gap: 8px
    min-width: 0

  .editor-actions-right
    flex-shrink: 0

  .json-editor-wrap
    background: #fff
    border: 1px solid #ebeef5
    border-radius: 6px
    padding: 8px
    height: 520px
    max-height: 60vh
    overflow: hidden

  .department-json-editor
    display: block

    .jsoneditor
      height: 480px
      min-height: 280px
      border: none

    .jsoneditor-mode-code .ace-jsoneditor
      height: 480px !important
      min-height: 280px !important

    .jsoneditor-mode-text textarea.jsoneditor-text
      height: 480px !important
      min-height: 280px !important

  .category-title-row
    display: flex
    align-items: center
    flex-wrap: wrap
    gap: 6px
    min-width: 0

  .category-order-btns
    .el-button
      padding: 4px 6px
      margin-left: 0

  .menu-order-btns
    display: flex
    flex-direction: column
    margin-right: 2px

  .order-mini-btn
    padding: 0 4px !important
    min-width: auto
    margin-left: 0 !important

  .button
    display: flex
    flex-wrap: wrap
    gap: 8px

    .item
      padding: 10px 20px
      text-align: center
      cursor: pointer
      border-radius: 4px
      font-size: 14px
      transition: all 0.3s ease
      user-select: none
      margin-right: 0
      margin-bottom: 0

    .copy-icon-btn
      background: #10b981
      color: #ffffff
      border: 1px solid #10b981

      &:hover
        background: #34d399
        border-color: #34d399
        transform: translateY(-1px)
        box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3)

    .save-btn
      background: #2563eb
      color: #ffffff
      border: 1px solid #2563eb

      &:hover
        background: #66b1ff
        border-color: #66b1ff
        transform: translateY(-1px)
        box-shadow: 0 2px 8px rgba(64, 158, 255, 0.3)
</style>

<style scoped>
/* 头像/图标 上传组件样式（原生 CSS） */
.avatar-uploader>>>.el-upload {
	border: 1px dashed #d9d9d9;
	border-radius: 6px;
	cursor: pointer;
	position: relative;
	overflow: hidden;
	background-color: #fbfdff;
}

.avatar-uploader>>>.el-upload:hover {
	border-color: #2563eb;
}

.avatar-uploader-icon {
	font-size: 28px;
	color: #8c939d;
	width: 80px;
	height: 80px;
	line-height: 80px;
	text-align: center;
}

.avatar {
	width: 80px;
	height: 80px;
	display: block;
	object-fit: contain;
}

/* 窄屏/分栏/移动端深度响应式排版与布局优化 */
@media (max-width: 992px) {
	.department-config-page>>>.menu-grid {
		grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
		gap: 10px;
	}

	.department-config-page>>>.editor-hint {
		margin-left: 0;
		width: 100%;
	}

	::v-deep .el-dialog {
		width: 95% !important;
		margin-top: 5vh !important;
	}

	::v-deep .el-dialog .el-dialog__body>div {
		flex-direction: column !important;
		align-items: center !important;
		gap: 20px !important;
	}

	::v-deep .preview-panel {
		width: 100% !important;
		max-width: 380px !important;
		margin-bottom: 10px;
	}

	::v-deep .el-dialog .el-form {
		width: 100% !important;
	}

	::v-deep .el-form-item {
		display: flex;
		flex-direction: column;
		align-items: flex-start !important;
		margin-bottom: 16px;
	}

	::v-deep .el-form-item__label {
		width: 100% !important;
		text-align: left !important;
		padding: 0 0 6px !important;
		line-height: 1.35 !important;
		font-weight: 600;
		color: #334155;
	}

	::v-deep .el-form-item__content {
		margin-left: 0 !important;
		width: 100% !important;
	}
}

@media (max-width: 640px) {
	.department-config-page>>>.menu-grid {
		grid-template-columns: 1fr;
	}

	.department-config-page>>>.menu-item {
		flex-wrap: wrap;
	}

	.department-config-page>>>.menu-switch {
		width: 100%;
		justify-content: flex-end;
		margin-top: 4px;
		padding-top: 8px;
		border-top: 1px dashed #ebeef5;
	}
}
</style>
